// frontend/components/settings/ProfileEditSection.tsx

"use client";

import { useState, useEffect } from "react";
import { signOut } from "next-auth/react";
import { KeyRound, Save, ChevronRight, RefreshCw } from "lucide-react";
import { API_URL, authHeaders } from "../../utils/api";
import ProfileChangeRequestModal from "./ProfileChangeRequestModal";

interface ProfileEditSectionProps {
  session: any;
}

export default function ProfileEditSection({ session }: ProfileEditSectionProps) {
  const user = session?.user as any;

  const [email, setEmail]   = useState(user?.email || "");
  const [phone, setPhone]   = useState(user?.phone || "");
  const [saveMsg, setSaveMsg] = useState("");

  // 비밀번호 변경 단계: 'closed' | 'step1' | 'step2'
  const [pwdStep, setPwdStep]       = useState<"closed" | "step1" | "step2">("closed");
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd]         = useState("");
  const [confirmPwd, setConfirm]    = useState("");
  const [pwdError, setPwdError]     = useState("");
  const [verifying, setVerifying]   = useState(false);

  // 비밀번호 초기화 요청 (step2에서만 노출)
  const [resetSent, setResetSent]   = useState(false);
  const [resetError, setResetError] = useState("");

  // 변경 요청 모달
  const [changeReqOpen, setChangeReqOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setPhone(user.phone || "");
    }
  }, [user?.id]);

  const handleSave = async () => {
    try {
      const res = await fetch(`${API_URL}/auth/profile`, {
        method: "PATCH",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({ email, phone }),
      });
      if (!res.ok) throw new Error();
      setSaveMsg("저장되었습니다.");
      setTimeout(() => setSaveMsg(""), 3000);
    } catch {
      setSaveMsg("저장에 실패했습니다.");
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const handleVerifyPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError("");
    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/auth/verify-password`, {
        method: "POST",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({ password: currentPwd }),
      });
      if (res.status === 401) return setPwdError("비밀번호가 올바르지 않습니다.");
      if (!res.ok) throw new Error();
      setPwdStep("step2");
    } catch {
      setPwdError("확인 중 오류가 발생했습니다.");
    } finally {
      setVerifying(false);
    }
  };

  const handlePwdChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdError("");
    if (newPwd !== confirmPwd) return setPwdError("비밀번호가 일치하지 않습니다.");
    if (newPwd.length < 8) return setPwdError("8자리 이상 입력해주세요.");
    try {
      const res = await fetch(`${API_URL}/auth/change-password`, {
        method: "POST",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({ currentPassword: currentPwd, newPassword: newPwd }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || "변경에 실패했습니다.");
      }
      alert("비밀번호가 변경되었습니다. 다시 로그인해주세요.");
      signOut({ callbackUrl: "/login" });
    } catch (err: any) {
      setPwdError(err.message || "변경에 실패했습니다.");
    }
  };

  const closePwd = () => {
    setPwdStep("closed");
    setCurrentPwd("");
    setNewPwd("");
    setConfirm("");
    setPwdError("");
    setResetSent(false);
    setResetError("");
  };

  const handleResetRequest = async () => {
    setResetError("");
    try {
      const res = await fetch(`${API_URL}/system/notify`, {
        method: "POST",
        headers: authHeaders(user?.accessToken),
        body: JSON.stringify({ type: "PASSWORD_RESET" }),
      });
      if (res.status === 429) return setResetError("1시간에 한 번만 요청할 수 있습니다.");
      if (!res.ok) throw new Error();
      setResetSent(true);
    } catch {
      setResetError("요청 전송에 실패했습니다.");
    }
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-700 rounded-lg outline-none focus:ring-1 focus:ring-blue-400 dark:focus:ring-blue-500 text-gray-800 dark:text-zinc-100";
  const readonlyCls = "w-full px-3 py-2 text-sm bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg text-gray-500 dark:text-zinc-400 cursor-not-allowed select-none";
  const labelCls = "block text-xs font-medium text-gray-500 dark:text-zinc-400 mb-1";

  return (
    <div className="space-y-5">
      {/* 아바타 */}
      <div className="flex flex-col items-center gap-2">
        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-2xl font-bold text-blue-600 dark:text-blue-300 select-none">
          {user?.name?.slice(0, 2) || user?.loginId?.slice(0, 2) || "U"}
        </div>
        <p className="text-[11px] text-gray-400 dark:text-zinc-500">프로필 사진 (추후 지원 예정)</p>
      </div>

      {/* 아이디 (읽기 전용) */}
      <div>
        <label className={labelCls}>아이디</label>
        <input value={user?.loginId || ""} readOnly className={readonlyCls} />
      </div>

      {/* 이름 (읽기 전용) */}
      <div>
        <label className={labelCls}>이름</label>
        <input value={user?.name || ""} readOnly className={readonlyCls} />
      </div>

      {/* 소속 + 변경 요청 버튼 */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className={`${labelCls} mb-0`}>소속 / 직책 / 부서장</label>
          <button
            type="button"
            onClick={() => setChangeReqOpen(true)}
            className="flex items-center gap-1 text-[11px] text-[#006DBA] dark:text-[#3399FF] hover:underline font-medium"
          >
            <ChevronRight size={11} />변경 요청
          </button>
        </div>
        <div className={readonlyCls}>
          {[
            user?.department,
            user?.position,
            user?.isDeptHead ? "부서장" : null,
          ]
            .filter(Boolean)
            .join(" · ") || "미설정"}
        </div>
      </div>

      {/* 비밀번호 변경 */}
      <div>
        <label className={labelCls}>비밀번호</label>

        {pwdStep === "closed" && (
          <button
            onClick={() => setPwdStep("step1")}
            className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 dark:border-zinc-700 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors text-gray-600 dark:text-zinc-300"
          >
            <span className="flex items-center gap-2"><KeyRound size={14} />비밀번호 변경</span>
          </button>
        )}

        {pwdStep === "step1" && (
          <form onSubmit={handleVerifyPassword} className="space-y-2 p-3 border border-gray-200 dark:border-zinc-700 rounded-lg bg-gray-50/50 dark:bg-zinc-800/30">
            <p className="text-xs text-gray-500 dark:text-zinc-400 font-medium">현재 비밀번호 확인</p>
            <input
              type="password"
              // value prop 의도적 생략(uncontrolled) — controlled input은 매 키 입력마다 실제
              // HTML value 속성에 값이 동기화되어 F12로 평문이 노출됨
              onChange={e => setCurrentPwd(e.target.value)}
              placeholder="현재 비밀번호"
              autoComplete="current-password"
              className={inputCls}
              autoFocus
              required
            />
            {pwdError && <p className="text-xs text-red-500">{pwdError}</p>}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={verifying}
                className="flex-1 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-60 text-white text-xs font-medium rounded-lg transition-colors"
              >
                {verifying ? "확인 중…" : "확인"}
              </button>
              <button type="button" onClick={closePwd} className="px-3 py-1.5 border border-gray-300 dark:border-zinc-600 text-xs rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors">
                취소
              </button>
            </div>

            {/* 현재 비밀번호를 모를 경우 초기화 요청 */}
            <div className="pt-1 border-t border-gray-100 dark:border-zinc-800 mt-1">
              {resetSent ? (
                <p className="text-xs text-green-600 dark:text-green-400 text-center">초기화 요청이 전송되었습니다.</p>
              ) : (
                <button
                  type="button"
                  onClick={handleResetRequest}
                  className="w-full flex items-center justify-center gap-1 text-xs text-gray-400 dark:text-zinc-500 hover:text-purple-500 dark:hover:text-purple-400 transition-colors py-1"
                >
                  <RefreshCw size={11} />비밀번호 초기화 요청
                </button>
              )}
              {resetError && <p className="text-xs text-red-500 text-center mt-1">{resetError}</p>}
            </div>
          </form>
        )}

        {pwdStep === "step2" && (
          <form onSubmit={handlePwdChange} className="space-y-2 p-3 border border-gray-200 dark:border-zinc-700 rounded-lg bg-gray-50/50 dark:bg-zinc-800/30">
            <p className="text-xs text-gray-500 dark:text-zinc-400 font-medium">새 비밀번호 입력</p>
            <input type="password" onChange={e => setNewPwd(e.target.value)} placeholder="새 비밀번호" autoComplete="new-password" className={inputCls} autoFocus required />
            <input type="password" onChange={e => setConfirm(e.target.value)} placeholder="비밀번호 확인" autoComplete="new-password" className={inputCls} required />
            {pwdError && <p className="text-xs text-red-500">{pwdError}</p>}
            <div className="flex gap-2 pt-1">
              <button type="submit" className="flex-1 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors">변경하기</button>
              <button type="button" onClick={closePwd} className="px-3 py-1.5 border border-gray-300 dark:border-zinc-600 text-xs rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors">취소</button>
            </div>
          </form>
        )}
      </div>

      {/* 이메일 (편집 가능) */}
      <div>
        <label className={labelCls}>이메일</label>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputCls} placeholder="이메일" />
      </div>

      {/* 연락처 (편집 가능) */}
      <div>
        <label className={labelCls}>연락처</label>
        <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className={inputCls} placeholder="010-0000-0000" />
      </div>

      {/* 저장 */}
      <div className="pt-1">
        <button
          onClick={handleSave}
          className="w-full flex items-center justify-center gap-1.5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Save size={14} />저장
        </button>
      </div>
      {saveMsg && <p className="text-xs text-center text-blue-500 dark:text-blue-400">{saveMsg}</p>}

      {/* 직급·소속 변경 요청 모달 */}
      <ProfileChangeRequestModal
        isOpen={changeReqOpen}
        onClose={() => setChangeReqOpen(false)}
        currentUser={{
          department: user?.department,
          position: user?.position,
          isDeptHead: user?.isDeptHead,
        }}
        accessToken={user?.accessToken}
      />
    </div>
  );
}
